
/**
 * Point avec animation pour le navigation-guide
 */
interface AnimatedPoint {
  // Position de départ
  startPosition: [number, number];
  // Position actuelle
  currentPosition: [number, number];
  // Position cible
  targetPosition: [number, number];
  // Cap actuel (en degrés)
  currentBearing: number;
  // Cap cible (en degrés)
  targetBearing: number;
  // Timestamp de début d'animation
  startTime: number;
  // Durée de l'animation (ms)
  duration: number;
}

/**
 * Gestionnaire d'animation pour le point de navigation
 */
export class NavigationPointAnimator {
  // État de l'animation en cours
  private animatedPoint: AnimatedPoint | null = null;

  // Fonction de callback pour mettre à jour la source sur la carte
  private readonly updateSourceCallback: ((position: [number, number], bearing: number) => void) | null = null;

  // ID de l'animation frame
  private animationFrameId: number | null = null;

  /**
   * Initialise l'animateur
   */
  constructor(updateCallback: (position: [number, number], bearing: number) => void) {
    this.updateSourceCallback = updateCallback;
  }

  /**
   * Démarre ou met à jour une animation vers une nouvelle position
   */
  public animateTo(
    targetPosition: [number, number],
    targetBearing: number,
    duration: number
  ): void {
    const now = performance.now();

    // Si pas d'animation en cours, initialiser avec la position cible actuelle
    if (this.animatedPoint === null) {
      this.animatedPoint = {
        startPosition: targetPosition,
        currentPosition: targetPosition,
        targetPosition,
        currentBearing: targetBearing,
        targetBearing,
        startTime: now,
        duration
      };

      // Appeler la callback immédiatement pour la position initiale
      if (this.updateSourceCallback != null) {
        this.updateSourceCallback(targetPosition, targetBearing);
      }

      return;
    }

    // Mettre à jour l'animation en cours
    this.animatedPoint = {
      // Garder la position et orientation actuelles
      startPosition: this.getCurrentPosition(),
      currentPosition: this.getCurrentPosition(),
      currentBearing: this.getCurrentBearing(),
      // Définir les nouvelles cibles
      targetPosition,
      targetBearing: this.normalizeBearingTransition(this.getCurrentBearing(), targetBearing),
      // Réinitialiser le timing
      startTime: now,
      duration
    };

    // Démarrer l'animation si pas déjà en cours
    if (this.animationFrameId === null) {
      this.startAnimation();
    }
  }

  /**
   * Normalise la transition entre deux angles pour prendre le chemin le plus court
   */
  private normalizeBearingTransition(currentBearing: number, targetBearing: number): number {
    const diff = ((targetBearing - currentBearing) % 360 + 540) % 360 - 180;
    return (currentBearing + diff) % 360;
  }

  /**
   * Démarre la boucle d'animation
   */
  private startAnimation(): void {
    const animate = (timestamp: number) => {
      if (this.animatedPoint == null) {
        this.animationFrameId = null;
        return;
      }

      const {
        targetPosition,
        startPosition,
        currentBearing, targetBearing,
        startTime, duration
      } = this.animatedPoint;

      // Calculer la progression (0 à 1)
      const elapsed = timestamp - startTime;
      const progress = Math.min(1, elapsed / duration);

      // Fonction d'easing (la même que celle utilisée pour la carte)
      const easedProgress = progress; /* < 0.5
        ? 2 * progress * progress
        : -1 + (4 - 2 * progress) * progress;*/

      // Interpoler la position
      const newPosition: [number, number] = [
        startPosition[0] + (targetPosition[0] - startPosition[0]) * easedProgress,
        startPosition[1] + (targetPosition[1] - startPosition[1]) * easedProgress
      ];

      // Interpoler le cap (en tenant compte de la cyclicité des angles)
      const bearingDiff = ((targetBearing - currentBearing) % 360 + 360) % 360;
      const shortestDiff = bearingDiff > 180 ? bearingDiff - 360 : bearingDiff;
      const newBearing = (currentBearing + shortestDiff * easedProgress + 360) % 360;

      // Mettre à jour la source
      if (this.updateSourceCallback != null) {
        this.updateSourceCallback(newPosition, newBearing);
      }

      // Continuer l'animation si pas terminée
      if (progress < 1) {
        this.animatedPoint.currentPosition = newPosition;
        this.animatedPoint.currentBearing = newBearing;
        this.animationFrameId = requestAnimationFrame(animate);
      } else {
        // Animation terminée
        this.animationFrameId = null;

        // Mettre à jour l'état avec les valeurs finales exactes
        if (this.animatedPoint !== undefined) {
          this.animatedPoint.currentPosition = this.animatedPoint.targetPosition;
          this.animatedPoint.currentBearing = this.animatedPoint.targetBearing;
        }
      }
    };

    // Démarrer la boucle d'animation
    this.animationFrameId = requestAnimationFrame(animate);
  }

  /**
   * Arrête l'animation en cours
   */
  public stop(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Récupère la position actuelle de l'animation
   */
  private getCurrentPosition(): [number, number] {
    if (this.animatedPoint == null) {
      return [0, 0];
    }
    return [...this.animatedPoint.currentPosition];
  }

  /**
   * Récupère le cap actuel de l'animation
   */
  private getCurrentBearing(): number {
    if (this.animatedPoint == null) {
      return 0;
    }
    return this.animatedPoint.currentBearing;
  }
}
